import { 
  getAuth, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  browserLocalPersistence,
  setPersistence,
  createUserWithEmailAndPassword,
  updateProfile,
  linkWithPopup,
  deleteUser,
  type User
} from 'firebase/auth';

import { 
  getFirestore, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  arrayUnion
} from 'firebase/firestore';

import { IAuthRepository, type AuthResult } from '../../domain/repositories/IAuthRepository';
import { FirebaseProvider } from '../providers/FirebaseProvider';
import type { CompanyRef, UserData } from '../../domain/types/User';
import type { OnboardingData } from '../../domain/types/Onboarding';
import type { ICompanyConfigRepository } from '../../domain/repositories/ICompanyConfigRepository';
import { FirestoreCompanyConfigRepository } from './FirestoreCompanyConfigRepository';
import { ISubscriptionPlan, Plan } from '@/domain/types';
import { userEvents } from '@/config/analytics';
import type { InviteData } from '@/domain/types/Company';
import { clientTypeForRole, resolveUserRole, type CompanyRole, type ModulePermissions } from '@/domain/types/Role';
import { canEmailJoinCompany, EmailDomainNotAllowedError } from '@/utils/emailDomains';

export class FirebaseAuthRepository implements IAuthRepository {
  private auth;
  private db;
  private googleProvider;
  private microsoftProvider;
  private configRepository: ICompanyConfigRepository;
  private defaultPlanId: string = "az3qFtboiMPUnI7t3SoW";

  constructor() {
    // La persistencia (localStorage) queda fijada al crear el Auth en
    // FirebaseProvider. Aquí no se llama setPersistence: hacerlo en cada
    // arranque movía la sesión de almacén y abría una ventana en la que otra
    // pestaña arrancando no la encontraba (KILA-370).
    this.auth = getAuth(FirebaseProvider.getInstance().getApp());
    this.db = getFirestore(FirebaseProvider.getInstance().getApp());
    this.googleProvider = new GoogleAuthProvider();
    this.microsoftProvider = new OAuthProvider('microsoft.com');
    this.configRepository = new FirestoreCompanyConfigRepository();
  }

  /**
   * `null` significa exactamente una cosa: el documento no existe.
   *
   * Antes el catch se tragaba cualquier error y también devolvía `null`, así
   * que un fallo de red era indistinguible de "este usuario no existe" y
   * terminaba expulsando al login a alguien con la sesión intacta. Ahora el
   * error se propaga y quien llama decide.
   */
  public async getUserData(userId: string): Promise<UserData | null> {
    // getDoc y no getDocFromServer: forzar red convierte cualquier corte
    // transitorio en pérdida de sesión.
    const userDoc = await getDoc(doc(this.db, 'Users', userId));

    if (userDoc.exists()) {
      return this.normalizeRoleFields(userDoc.data() as UserData);
    }

    console.warn(`⚠️ User not found in Firestore: ${userId}`)
    return null;
  }


  /**
   * La condición de "solo vista" vive hoy en dos campos, role y clientType, y
   * quedan roles del modelo anterior en Firestore. Se resuelven al leer para que
   * toda la app vea el rol vigente y para que los chequeos que siguen usando
   * clientType (checkClientType, useViewerConfig) sigan siendo válidos.
   */
  private normalizeRoleFields(userData: UserData): UserData {
    const role = resolveUserRole(userData.role, userData.clientType);
    if (!role) return userData;

    return { ...userData, role, clientType: clientTypeForRole(role) };
  }

  private async mapUserToAuthResult(user: User | null): Promise<UserData | null> {
    if (!user) return null;
    
    const userData = await this.getUserData(user.uid);
    
    return userData;
  }

  private async allowedDomainsOf(companyId: string): Promise<string[]> {
    const companyDoc = await getDoc(doc(this.db, 'Companies', companyId));
    return companyDoc.exists() ? (companyDoc.data().allowedEmailDomains as string[] | undefined) || [] : [];
  }

  /**
   * Los dominios permitidos también se revisan al entrar, no solo al registrarse:
   * si el admin restringe la compañía después, quien deja de cumplir no vuelve a
   * entrar en ella. Si el usuario tiene otra compañía que sí lo acepta se le mueve
   * allí en vez de dejarlo afuera, y si no hay ninguna se cierra la sesión.
   * El superadmin es soporte de la plataforma y nunca se bloquea.
   */
  private async enforceDomainPolicyOnSession(userData: UserData): Promise<UserData> {
    const email = userData.email || '';
    const activeCompanyId = userData.companyId;
    if (!email || !activeCompanyId || userData.role === 'superadmin') return userData;

    const emailVerified = this.auth.currentUser?.emailVerified ?? false;
    const domains = await this.allowedDomainsOf(activeCompanyId);
    if (canEmailJoinCompany(email, domains, emailVerified)) return userData;

    for (const company of userData.companiesRef || []) {
      if (company.id === activeCompanyId) continue;
      if (canEmailJoinCompany(email, await this.allowedDomainsOf(company.id), emailVerified)) {
        await updateDoc(doc(this.db, 'Users', userData.uid), { companyId: company.id });
        return { ...userData, companyId: company.id };
      }
    }

    await signOut(this.auth);
    throw new EmailDomainNotAllowedError(domains);
  }

  private handleFirebaseAuthError(error: any): string {
    const errorCode = error.code;
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'El correo electrónico no es válido';
      case 'auth/user-disabled':
        return 'Esta cuenta ha sido deshabilitada';
      // Mensaje genérico unificado para no permitir enumeración de cuentas
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return 'Correo electrónico o contraseña incorrectos';
      case 'auth/email-already-in-use':
        return 'Este correo electrónico ya está registrado';
      case 'auth/operation-not-allowed':
        return 'Operación no permitida';
      case 'auth/weak-password':
        return 'La contraseña debe tener al menos 6 caracteres';
      case 'auth/network-request-failed':
        return 'Error de conexión. Por favor, verifica tu conexión a internet';
      case 'auth/popup-closed-by-user':
        return 'Se cerró la ventana de inicio de sesión';
      case 'auth/credential-already-in-use':
        return 'Esta cuenta ya está vinculada a otro usuario';
      case 'auth/requires-recent-login':
        return 'Esta operación es sensible y requiere autenticación reciente. Inicia sesión nuevamente antes de intentarlo';
      case 'auth/cancelled-popup-request':
        return 'Se canceló la operación';
      case 'auth/popup-blocked':
        return 'El navegador bloqueó la ventana emergente. Por favor, permite ventanas emergentes para continuar';
      default:
        return 'Error al intentar iniciar sesión. Por favor, intenta nuevamente';
    }
  }

  async login(email: string, password: string): Promise<UserData | null> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const mappedUser = await this.mapUserToAuthResult(userCredential.user);
      if (!mappedUser) {
        throw new Error('Usuario no encontrado en la base de datos');
      }
      return await this.enforceDomainPolicyOnSession(mappedUser);
    } catch (error: any) {
      if (error instanceof EmailDomainNotAllowedError) throw error;
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }

  private async createOAuthUser(
    userCredential: User,
    inviteData?: InviteData
  ): Promise<AuthResult> {
    try {
      // Obtener el nombre de la compañía si existe
      let companyRef: CompanyRef | null = null
      if (inviteData?.companyId) {
        const companyDoc = await getDoc(doc(this.db, 'Companies', inviteData.companyId))
        if (companyDoc.exists()) {
          const companyData = companyDoc.data()
          const companyName = companyData?.name || inviteData.companyId
          
          companyRef = {
            id: inviteData.companyId,
            name: companyName,
            code: "001",
            ref: `/Companies/${inviteData.companyId}`
          }
        }
      }

      // Sin invitación el usuario crea su propia compañía, así que es su admin.
      const role: CompanyRole = inviteData?.role || 'admin'

      const newUserData: UserData = {
        uid: userCredential.uid,
        email: userCredential.email || '',
        name: userCredential.displayName?.split(' ')[0] || '',
        lastName: userCredential.displayName?.split(' ').slice(1).join(' ') || '',
        role,
        dash: null,
        hasChildren: false,
        childrenRef: null,
        hasParent: null,
        parentRef: null,
        profile: 'default',
        phone: null,
        permissions: inviteData?.companyId && inviteData.permissions
          ? { [inviteData.companyId]: inviteData.permissions }
          : {},
        companyId: inviteData?.companyId || '',
        companiesRef: companyRef ? [companyRef] : [],
        companyIds: inviteData?.companyId ? [inviteData.companyId] : [],
        clientType: clientTypeForRole(role),
        themeConfig: [],
        subscriptionId: 'TtjNFaCuAd5D9wuWbp1Z',
        plan: {
          planId: 'free',
          startDate: new Date(),
          status: 'active'
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active',
        hasCompletedOnboarding: inviteData?.companyId ? true : false
      };

      // Create user document in Firestore
      const userDoc = doc(this.db, 'Users', userCredential.uid);
      await setDoc(userDoc, newUserData);

      // Si hay una compañía, agregar el usuario a la lista de usuarios de la compañía
      if (inviteData?.companyId) {
        await updateDoc(doc(this.db, 'Companies', inviteData.companyId), {
          users: arrayUnion({
            id: userCredential.uid,
            email: userCredential.email,
            name: newUserData.name,
            lastName: newUserData.lastName,
            role: 'user'
          })
        })
      }

      // Inicializar usageHistory
      await this.initializeUsageHistory(userCredential.uid)

      // Create subscription
      await this.subscribeToPlan(this.defaultPlanId, userCredential.uid);

      userEvents.userCreated(userCredential.uid)

      return {
        success: true,
        user: newUserData
      };
    } catch (error: any) {
      console.error('Error creating OAuth user:', error);
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }

  private async subscribeToPlan( planId: string, uid: string ) {
    
      // Fetch plan information
      const planDoc = await getDoc(doc(this.db, 'Plans', planId));
      if (!planDoc.exists()) {
        throw new Error('Default plan not found');
      }
      
      const plan = planDoc.data() as Plan;
      
      // Create subscription
      const now = new Date().toISOString();
      const subscriptionData: ISubscriptionPlan = {
        cancelAtPeriodEnd: false,
        createdAt: now,
        planId: planId,
        renewalDate: null,
        active: true,
        trialEndDate: plan.trialPeriodDays ? new Date(Date.now() + plan.trialPeriodDays * 24 * 60 * 60 * 1000).toISOString() : null,
        trialStartDate: plan.trialPeriodDays ? now : null,
        updatedAt: now,
        userId: uid
      };

      // Create subscription document
      await setDoc(doc(this.db, 'Subscriptions', uid), subscriptionData);
  }

  async loginWithProvider(
    provider: 'google' | 'microsoft',
    inviteData?: InviteData
  ): Promise<UserData | null> {
    try {
      const authProvider = provider === 'google' ? this.googleProvider : this.microsoftProvider;
      const userCredential = await signInWithPopup(this.auth, authProvider);
      
      
      // Check if user exists in Firestore
      let mappedUser = await this.mapUserToAuthResult(userCredential.user);

      // Un enlace de invitación no alcanza: el correo tiene que ser de un dominio
      // que la compañía autorice.
      if (inviteData?.companyId && mappedUser?.role !== 'superadmin') {
        const domains = await this.allowedDomainsOf(inviteData.companyId);
        if (!canEmailJoinCompany(userCredential.user.email || '', domains, userCredential.user.emailVerified)) {
          // Cuenta que aún no pertenece a nada: no dejar la sesión colgada.
          if (!mappedUser) await signOut(this.auth);
          throw new EmailDomainNotAllowedError(domains);
        }
      }
      
      // CASO A: Usuario NO existe → Crear con inviteData
      if (!mappedUser) {
        if (inviteData) {
        }
        const result = await this.createOAuthUser(userCredential.user, inviteData);
        mappedUser = result.user || null;
      } else if (inviteData?.companyId) {
        // ✅ Usuario EXISTENTE con enlace de invitación
        
        // Verificar si el usuario ya tiene esta compañía
        const userCompaniesRef = mappedUser.companiesRef || [];
        const hasCompany = userCompaniesRef.some((c: any) => c.id === inviteData.companyId);
        
        if (!hasCompany) {
          
          // 1. Obtener datos de la compañía desde Firestore
          const companyDoc = await getDoc(doc(this.db, 'Companies', inviteData.companyId));
          if (!companyDoc.exists()) {
            throw new Error(`Compañía ${inviteData.companyId} no encontrada`);
          }
          
          const companyData = companyDoc.data();
          
          // 2. Agregar compañía al array companiesRef del usuario
          const newCompanyRef = {
            code: companyData.code || '001',
            id: inviteData.companyId,
            name: companyData.name || inviteData.companyId,
            ref: `/Companies/${inviteData.companyId}`
          };
          
          const updatedCompaniesRef = [...userCompaniesRef, newCompanyRef];

          // El rol es global: a un usuario que ya existe no se le cambia por
          // aceptar una invitación, o entrar a una compañía nueva lo degradaría
          // en las que ya tenía. Solo se le aplican los permisos de esta.
          await updateDoc(doc(this.db, 'Users', userCredential.user.uid), {
            companiesRef: updatedCompaniesRef,
            companyIds: updatedCompaniesRef.map((c) => c.id),
            companyId: inviteData.companyId, // ✅ Setear como compañía activa
            ...(inviteData.permissions
              ? {
                  permissions: {
                    ...(mappedUser.permissions || {}),
                    [inviteData.companyId]: inviteData.permissions
                  }
                }
              : {}),
            updatedAt: new Date().toISOString()
          });
          
          // 4. Agregar usuario a la lista de usuarios de la compañía
          await updateDoc(doc(this.db, 'Companies', inviteData.companyId), {
            users: arrayUnion({
              id: userCredential.user.uid,
              email: userCredential.user.email,
              name: mappedUser.name,
              lastName: mappedUser.lastName,
              role: 'user'
            })
          });
          
          // 5. Actualizar mappedUser con los nuevos datos
          mappedUser = {
            ...mappedUser,
            companiesRef: updatedCompaniesRef,
            companyIds: updatedCompaniesRef.map((c) => c.id),
            companyId: inviteData.companyId
          };

        } else {
          // La compañía ya existe, solo actualizar companyId
          
          await updateDoc(doc(this.db, 'Users', userCredential.user.uid), {
            companyId: inviteData.companyId,
            updatedAt: new Date().toISOString()
          });
          
          mappedUser = {
            ...mappedUser,
            companyId: inviteData.companyId
          };
        }
      }
      // CASO B: Usuario SÍ existe + tiene inviteData → Agregar compañía
      else if (inviteData?.companyId) {
        
        const userCompaniesRef = mappedUser.companiesRef || []
        const hasCompany = userCompaniesRef.some(c => c.id === inviteData.companyId)
        
        // Solo agregar si el usuario no tiene ya esta compañía
        if (!hasCompany) {
          
          // Obtener datos de la compañía
          const companyDoc = await getDoc(doc(this.db, 'Companies', inviteData.companyId))
          if (companyDoc.exists()) {
            const companyData = companyDoc.data()
            const companyName = companyData?.name || inviteData.companyId
            
            const newCompanyRef: CompanyRef = {
              id: inviteData.companyId,
              name: companyName,
              code: "001",
              ref: `/Companies/${inviteData.companyId}`
            }
            
            // Actualizar usuario con nueva compañía
            const userDoc = doc(this.db, 'Users', userCredential.user.uid)
            await updateDoc(userDoc, {
              companiesRef: arrayUnion(newCompanyRef),
              companyId: inviteData.companyId,
              hasCompletedOnboarding: true
            })
            
            // Agregar usuario al array de usuarios de la compañía
            const companyDocRef = doc(this.db, 'Companies', inviteData.companyId)
            await updateDoc(companyDocRef, {
              users: arrayUnion(userCredential.user.uid)
            })
            
            // Recargar usuario actualizado
            mappedUser = await this.mapUserToAuthResult(userCredential.user)
          }
        } else {
          // Usuario ya tiene la compañía, solo actualizar companyId activo
          const userDoc = doc(this.db, 'Users', userCredential.user.uid)
          await updateDoc(userDoc, {
            companyId: inviteData.companyId,
            hasCompletedOnboarding: true
          })
          
          mappedUser = await this.mapUserToAuthResult(userCredential.user)
        }
      }

      return mappedUser ? await this.enforceDomainPolicyOnSession(mappedUser) : mappedUser;
    } catch (error: any) {
      console.error('Error in loginWithProvider:', error);
      if (error instanceof EmailDomainNotAllowedError) throw error;
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }

  async logout(): Promise<void> {
    try {
      // Cerrar sesión directamente (signOut ya limpia la sesión correctamente)
      await signOut(this.auth);
      
      // Limpiamos datos locales de Firebase
      localStorage.removeItem('firebase:previous_websocket_failure');
      
      // Limpiamos cualquier otro dato específico de Firebase y nuestra app
      const firebaseKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('firebase:') || key.startsWith('subscription_')
      );
      firebaseKeys.forEach(key => localStorage.removeItem(key));
      
      // CRÍTICO: Re-establecer persistencia local para próximo login
      await setPersistence(this.auth, browserLocalPersistence);
      
    } catch (error) {
      console.error('Error during Firebase signOut:', error);
      // Asegurar que la persistencia esté correcta incluso si hay error
      try {
        await setPersistence(this.auth, browserLocalPersistence);
      } catch (persistError) {
        console.error('Error resetting persistence:', persistError);
      }
      throw error;
    }
  }

  async getCurrentUser(): Promise<UserData | null> {
    const currentUser = this.auth.currentUser;
    
    if (!currentUser) return null;
    
    const userData = await this.getUserData(currentUser.uid);
    if (!userData) return null;

    try {
      return await this.enforceDomainPolicyOnSession(userData);
    } catch (error) {
      // Solo este error significa "la política dice que no": la sesión ya se
      // cerró dentro de la política y el guard manda al login. Cualquier otro
      // error es "no pude evaluar la política" (red, permisos) y no puede
      // disfrazarse de rechazo, que era lo que expulsaba a usuarios legítimos.
      if (error instanceof EmailDomainNotAllowedError) return null;
      throw error;
    }
  }

  private async initializeUsageHistory(userId: string): Promise<void> {
    try {
      const currentDate = new Date()
      const documentId = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`
      
      const usageHistoryRef = doc(collection(this.db, 'Users', userId, 'usageHistory'), documentId)
      
      const usageData = {
        shipmentUsage: 0,
        createdAt: currentDate,
        lastUpdate: currentDate,
      }
      
      await setDoc(usageHistoryRef, usageData)
      
      // Verificar que se haya creado
      const docSnap = await getDoc(usageHistoryRef)
      if (!docSnap.exists()) {
        throw new Error('No se pudo crear el documento de usageHistory');
      }
    } catch (error) {
      console.error('Error al crear usageHistory:', error);
      throw error;
    }
  }

  async createUser(userData: {
    email: string
    password: string
    name: string
    lastName: string
    hasCompletedOnboarding: boolean
    companyId?: string | null
    role?: CompanyRole
    permissions?: ModulePermissions
  }): Promise<AuthResult> {
    try {
      // Crear usuario en Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        userData.email,
        userData.password
      )
      
      // Actualizar el perfil con nombre completo
      await updateProfile(userCredential.user, {
        displayName: `${userData.name} ${userData.lastName}`
      })

      // Obtener el nombre de la compañía si existe
      let companyName = ''
      let companyRef: CompanyRef | null = null
      if (userData.companyId) {
        const companyDoc = await getDoc(doc(this.db, 'Companies', userData.companyId))
        if (companyDoc.exists()) {
          const companyData = companyDoc.data()

          // Si el dominio no está autorizado se deshace la cuenta recién creada,
          // así el correo no queda ocupado en Auth sin haber entrado a la compañía.
          // emailVerified es false en una cuenta recién creada, así que aquí el
          // bypass del equipo nunca aplica: kila.co entra por Google, no por aquí.
          const domains: string[] = companyData?.allowedEmailDomains || []
          if (!canEmailJoinCompany(userData.email, domains, userCredential.user.emailVerified)) {
            await deleteUser(userCredential.user)
            throw new EmailDomainNotAllowedError(domains)
          }

          companyName = companyData?.name || userData.companyId
          
          companyRef = {
            id: userData.companyId,
            name: companyName,
            code: "001",
            ref: `/Companies/${userData.companyId}`
          }
        }
      }
      // Sin invitación el usuario crea su propia compañía, así que es su admin.
      const role: CompanyRole = userData.role || 'admin'

      // Crear documento de usuario en Firestore
      const newUserData: UserData = {
        uid: userCredential.user.uid,
        email: userData.email,
        name: userData.name,
        lastName: userData.lastName,
        role,
        dash: null,
        hasChildren: false,
        childrenRef: null,
        hasParent: null,
        parentRef: null,
        profile: 'default',
        clientType: clientTypeForRole(role),
        phone: null,
        permissions: userData.companyId && userData.permissions
          ? { [userData.companyId]: userData.permissions }
          : {},
        companyId: userData.companyId || '',
        companiesRef: companyRef ? [companyRef] : [],
        companyIds: userData.companyId ? [userData.companyId] : [],
        themeConfig: [],
        subscriptionId: 'TtjNFaCuAd5D9wuWbp1Z',
        plan: {
          planId: 'free',
          startDate: new Date(),
          status: 'active'
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active',
        hasCompletedOnboarding: userData.hasCompletedOnboarding
      };
      // Crear el documento del usuario
      await setDoc(doc(this.db, 'Users', userCredential.user.uid), newUserData)

      // Si hay una compañía, agregar el usuario a la lista de usuarios de la compañía
      if (userData.companyId) {
        await updateDoc(doc(this.db, 'Companies', userData.companyId), {
          users: arrayUnion({
            id: userCredential.user.uid,
            email: userData.email,
            name: userData.name,
            lastName: userData.lastName,
            role: 'user'
          })
        })
      }
      userEvents.userCreated(userCredential.user.uid)
      // Resultado a devolver
      const result = {
        success: true,
        user: newUserData
      };

      
      
      // Inicializar historial de uso en segundo plano (después de devolver la respuesta)
      setTimeout(() => {
        this.subscribeToPlan(this.defaultPlanId, userCredential.user.uid);
        this.initializeUsageHistory(userCredential.user.uid);
      }, 100);
      return result
    } catch (error: any) {
      console.error('Error en createUser:', error)
      // Propagamos el error original de Firebase
      if (error.code) {
        throw error
      }
      // Si no es un error de Firebase, usamos el handler
      throw new Error(this.handleFirebaseAuthError(error))
    }
  }

  async updateOnboardingData(userId: string, data: OnboardingData): Promise<void> {
    try {
      // Transform company name (uppercase and replace spaces with underscores)
      const formattedCompanyName = data.companyName
        .toUpperCase()
        .replace(/[^\w\s-_]/g, '')
        .replace(/[\s-/]/g, '_');
      
      const userDoc = doc(this.db, 'Users', userId);
      const companyDoc = doc(this.db, 'Companies', formattedCompanyName);
      
      // Create company document with onboarding data
      await setDoc(companyDoc, {
        name: data.companyName,
        industry: data.industry === "otro" ? data.otherIndustry : data.industry,
        companySize: data.companySize,
        shippingVolume: data.shippingVolume,
        operationType: data.operationType || 'importer',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: userId
      });

      // Initialize company configurations using the config repository
      await this.configRepository.initializeCompanyConfigs(formattedCompanyName, userId, data.operationType || 'importer');
      
      // Update user's onboarding data with new fields
      await updateDoc(userDoc, {
        onboardingData: data,
        hasCompletedOnboarding: true,
        companyId: formattedCompanyName,
        companiesRef: [{
          code: "001",
          name: data.companyName,
          id: formattedCompanyName,
          ref: `/Companies/${formattedCompanyName}`
        }],
        companyIds: [formattedCompanyName],
        updatedAt: new Date()
      });
      
    } catch (error: any) {
      console.error('Error updating onboarding data:', error);
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }

  async updateUser(userId: string, userData: Partial<UserData>): Promise<UserData> {
    try {
      const userDoc = doc(this.db, 'Users', userId);
      
      // Actualizar solo los campos proporcionados
      const updateData = {
        ...userData,
        updatedAt: new Date()
      };
      
      await updateDoc(userDoc, updateData);
      
      // Obtener el usuario actualizado
      const updatedUserDoc = await getDoc(userDoc);
      if (!updatedUserDoc.exists()) {
        throw new Error('Usuario no encontrado');
      }
      
      return this.normalizeRoleFields(updatedUserDoc.data() as UserData);
    } catch (error: any) {
      console.error('Error updating user:', error);
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }

  /**
   * Vincula una cuenta existente autenticada con email/password con un proveedor OAuth (Google o Microsoft)
   * El usuario debe haber iniciado sesión previamente para usar esta función
   */
  async linkAccountWithProvider(provider: 'google' | 'microsoft'): Promise<UserData | null> {
    try {
      // Verificar que haya un usuario actualmente autenticado
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('No hay una sesión activa. Debes iniciar sesión primero.');
      }

      // Seleccionar el proveedor adecuado
      const authProvider = provider === 'google' ? this.googleProvider : this.microsoftProvider;
      
      
      // Vincular la cuenta actual con el proveedor seleccionado
      const result = await linkWithPopup(currentUser, authProvider);
      
      
      // Actualizar el usuario en Firestore para reflejar los nuevos métodos de autenticación
      const userData = await this.getUserData(currentUser.uid);
      if (userData) {
        // Actualizar información adicional si es necesario
        const updateData: Partial<UserData> = {
          updatedAt: new Date()
        };
        
        // Si hay un nombre de display del proveedor y el usuario no tiene nombre, actualizarlo
        if (result.user.displayName && (!userData.name || !userData.lastName)) {
          const nameParts = result.user.displayName.split(' ');
          if (!userData.name && nameParts.length > 0) {
            updateData.name = nameParts[0];
          }
          if (!userData.lastName && nameParts.length > 1) {
            updateData.lastName = nameParts.slice(1).join(' ');
          }
        }
        
        await this.updateUser(currentUser.uid, updateData);
        return await this.getUserData(currentUser.uid);
      }
      
      return userData;
    } catch (error: any) {
      console.error('Error al vincular cuenta:', error);
      
      // Manejo específico para el caso de que la cuenta ya esté vinculada a otro usuario
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('Esta cuenta de ' + provider + ' ya está vinculada a otro usuario. No es posible vincularla.');
      }
      
      throw new Error(this.handleFirebaseAuthError(error));
    }
  }
}